import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import ProtectedRoute from './ProtectedRoute';
import RouteErrorBoundary from 'components/RouteErrorBoundary';

// dashboard routing
const SmartLockerDashboard = Loadable(lazy(() => import('views/pages/smart-locker/dashboard')));

// Device
const DeviceList = Loadable(lazy(() => import('views/pages/smart-locker/device-list')));
const GeographicZones = Loadable(lazy(() => import('views/pages/smart-locker/geographic-zones')));
const GeographicFacilities = Loadable(lazy(() => import('views/pages/smart-locker/geographic-facilities')));
const BoxList = Loadable(lazy(() => import('views/pages/smart-locker/box-list')));
const FacilityDetails = Loadable(lazy(() => import('views/pages/smart-locker/facility-details')));
const DeviceOverdue = Loadable(lazy(() => import('views/pages/smart-locker/device-overdue')));
const DevicePickup = Loadable(lazy(() => import('views/pages/smart-locker/device-pickup')));
const DeviceHistory = Loadable(lazy(() => import('views/pages/smart-locker/device-history')));
const DeviceSMS = Loadable(lazy(() => import('views/pages/smart-locker/device-sms')));
const DeviceEmail = Loadable(lazy(() => import('views/pages/smart-locker/device-email')));
const EmailTemplates = Loadable(lazy(() => import('views/pages/smart-locker/email-templates')));
const SmsTemplates = Loadable(lazy(() => import('views/pages/smart-locker/sms-templates')));
const NotificationRecipientMatrix = Loadable(lazy(() => import('views/pages/smart-locker/notifications')));

// Order
const DispatchMonitor = Loadable(lazy(() => import('views/pages/smart-locker/dispatch-monitor')));
const OrderDelivery = Loadable(lazy(() => import('views/pages/smart-locker/order-delivery')));
const OrderPickup = Loadable(lazy(() => import('views/pages/smart-locker/order-pickup')));
const OrderDelivered = Loadable(lazy(() => import('views/pages/smart-locker/order-delivered')));
const OrderOverdue = Loadable(lazy(() => import('views/pages/smart-locker/order-overdue')));

// Logistics & Linehaul
const LogisticsHubs = Loadable(lazy(() => import('views/pages/smart-locker/logistics-hubs')));
const LogisticsVehicles = Loadable(lazy(() => import('views/pages/smart-locker/logistics-vehicles')));
const LogisticsTrips = Loadable(lazy(() => import('views/pages/smart-locker/logistics-trips')));

// Payment
const PaymentDelivery = Loadable(lazy(() => import('views/pages/smart-locker/payment-delivery')));
const PaymentPickup = Loadable(lazy(() => import('views/pages/smart-locker/payment-pickup')));
const PaymentScheduled = Loadable(lazy(() => import('views/pages/smart-locker/payment-scheduled')));
const PaymentAdditionalOrder = Loadable(lazy(() => import('views/pages/smart-locker/payment-additional-order')));
const PaymentDeliveryOrder = Loadable(lazy(() => import('views/pages/smart-locker/payment-delivery-order')));
const PaymentPickupOrder = Loadable(lazy(() => import('views/pages/smart-locker/payment-pickup-order')));

// User
const UserManager = Loadable(lazy(() => import('views/pages/smart-locker/user-manager')));
const CourierOrganization = Loadable(lazy(() => import('views/pages/smart-locker/courier-organization')));
const UserEmployee = Loadable(lazy(() => import('views/pages/smart-locker/user-employee')));
const UserCustomer = Loadable(lazy(() => import('views/pages/smart-locker/user-customer')));
const UserDevice = Loadable(lazy(() => import('views/pages/smart-locker/user-device')));

// API
const ApiKey = Loadable(lazy(() => import('views/pages/smart-locker/api-key')));
const ApiDevice = Loadable(lazy(() => import('views/pages/smart-locker/api-device')));
const ApiInterface = Loadable(lazy(() => import('views/pages/smart-locker/api-interface')));

// Client Settings
const ClientMain = Loadable(lazy(() => import('views/pages/smart-locker/client-main')));
const ClientVersion = Loadable(lazy(() => import('views/pages/smart-locker/client-version')));

// Profile & System
const Profile = Loadable(lazy(() => import('views/pages/smart-locker/profile')));
const SystemConfigs = Loadable(lazy(() => import('views/pages/smart-locker/system-configs')));
const SecuritySettings = Loadable(lazy(() => import('views/pages/smart-locker/security-settings')));
const AccessGovernance = Loadable(lazy(() => import('views/pages/smart-locker/access-governance')));
const AuditLogs = Loadable(lazy(() => import('views/pages/smart-locker/audit-logs')));

// utilities routing
const UtilsTypography = Loadable(lazy(() => import('views/utilities/Typography')));
const UtilsColor = Loadable(lazy(() => import('views/utilities/Color')));
const UtilsShadow = Loadable(lazy(() => import('views/utilities/Shadow')));

// sample page routing
const SamplePage = Loadable(lazy(() => import('views/sample-page')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <ProtectedRoute><MainLayout /></ProtectedRoute>,
  errorElement: <RouteErrorBoundary />,
  children: [
    { path: '/', element: <SmartLockerDashboard /> },
    { 
      path: 'dashboard', 
      children: [
        { index: true, element: <SmartLockerDashboard /> },
        { path: 'default', element: <SmartLockerDashboard /> }
      ] 
    },
    
    // Smart Locker Routes
    { path: 'profile', element: <Profile /> },
    { path: 'account-settings', element: <Profile /> },
    { path: 'system-configs', element: <SystemConfigs /> },
    { path: 'security', element: <SecuritySettings /> },
    { path: 'security/access-governance', element: <AccessGovernance /> },
    { path: 'access-governance', element: <AccessGovernance /> },
    { path: 'system/audit-logs', element: <AuditLogs /> },
    { path: 'system/zones', element: <GeographicZones /> },
    
    { path: 'device', children: [
        { path: 'list', element: <DeviceList /> },
        { path: 'facilities', element: <GeographicFacilities /> },
        { path: 'facility/:id', element: <FacilityDetails /> },
        { path: 'zones', element: <GeographicZones /> },
        { path: 'box', element: <BoxList /> },
        { path: 'overdue', element: <DeviceOverdue /> },
        { path: 'pickup', element: <DevicePickup /> },
        { path: 'history', element: <DeviceHistory /> },
        { path: 'sms', element: <DeviceSMS /> },
        { path: 'email', element: <DeviceEmail /> },
        { path: 'email-templates', element: <EmailTemplates /> },
        { path: 'sms-templates', element: <SmsTemplates /> },
        { path: 'notifications-matrix', element: <NotificationRecipientMatrix /> },
        { path: 'notification-matrix', element: <NotificationRecipientMatrix /> }
    ]},
    { path: 'order', children: [
        { path: 'dispatch-monitor', element: <DispatchMonitor /> },
        { path: 'delivery', element: <OrderDelivery /> },
        { path: 'pickup', element: <OrderPickup /> },
        { path: 'delivered', element: <OrderDelivered /> },
        { path: 'overdue', element: <OrderOverdue /> }
    ]},
    { path: 'logistics', children: [
        { path: 'hubs', element: <LogisticsHubs /> },
        { path: 'vehicles', element: <LogisticsVehicles /> },
        { path: 'trips', element: <LogisticsTrips /> }
    ]},
    { path: 'payment', children: [
        { path: 'delivery', element: <PaymentDelivery /> },
        { path: 'pickup', element: <PaymentPickup /> },
        { path: 'scheduled', element: <PaymentScheduled /> },
        { path: 'additional', element: <PaymentAdditionalOrder /> },
        { path: 'delivery-order', element: <PaymentDeliveryOrder /> },
        { path: 'pickup-order', element: <PaymentPickupOrder /> }
    ]},
    { path: 'users', children: [
        { path: 'profile', element: <Profile /> },
        { path: 'account-profile', element: <Profile /> },
        { path: 'manager', element: <UserManager /> },
        { path: 'courier-orgs', element: <CourierOrganization /> },
        { path: 'employee', element: <UserEmployee /> },
        { path: 'customer', element: <UserCustomer /> },
        { path: 'bind', element: <UserDevice /> }
    ]},
    { path: 'api', children: [
        { path: 'key', element: <ApiKey /> },
        { path: 'device', element: <ApiDevice /> },
        { path: 'interface', element: <ApiInterface /> }
    ]},
    { path: 'client', children: [
        { path: 'main', element: <ClientMain /> },
        { path: 'version', element: <ClientVersion /> }
    ]},

    // Utility & Misc
    { path: 'typography', element: <UtilsTypography /> },
    { path: 'color', element: <UtilsColor /> },
    { path: 'shadow', element: <UtilsShadow /> },
    { path: 'sample-page', element: <SamplePage /> }
  ]
};

export default MainRoutes;
